import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import FilmMark from "@/components/FilmMark";
import { useLang } from "@/i18n/LanguageContext";

export default function DonorsWallPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/donors/wall")
      .then((r) => setDonors(r.data.donors || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="pt-32 pb-24" data-testid="donors-wall-page">
      <FilmMark />

      <section className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "The donors' wall" : "Le mur des donateurs"} · Le Fil du Midi</div>
        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.88] tracking-tight mt-8">
          {en ? <>Every first name,<br /><em className="italic">a link.</em></> : <>Chaque prénom,<br /><em className="italic">un maillon.</em></>}
        </h1>
        <p className="mt-10 font-serif italic text-2xl md:text-3xl leading-[1.35] text-fg/75 max-w-3xl">
          {en ? "They made a gesture so that art can travel along the water. Discreet, sober, eternal — thank you." : "Ils ont fait un geste pour que l'art circule au bord de l'eau. Discret, sobre, éternel — merci."}
        </p>
      </section>

      <section className="mt-20 md:mt-28 px-6 md:px-12 max-w-[1400px] mx-auto">
        {loading && (
          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/45 animate-pulse">{en ? "Loading the wall…" : "Chargement du mur…"}</p>
        )}

        {!loading && donors.length > 0 && (
          <>
            <div className="meta-label text-cinabre mb-12">
              § {donors.length} {en ? (donors.length > 1 ? "donors" : "donor") : `donateur${donors.length > 1 ? "s" : ""}`} {en ? "along the water" : "au fil de l'eau"}
            </div>
            <div className="flex flex-wrap gap-x-10 gap-y-8 md:gap-x-14 md:gap-y-10 items-baseline" data-testid="donors-wall-names">
              {donors.map((d, i) => (
                <motion.span
                  key={`${d.first_name}-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (i % 12) * 0.04 }}
                  className={`font-serif font-light tracking-tight leading-none ${
                    i % 5 === 0 ? "text-4xl md:text-6xl text-cinabre" : i % 3 === 0 ? "text-3xl md:text-5xl" : "text-2xl md:text-4xl text-fg/80"
                  }`}
                  data-testid={`donor-name-${i}`}
                >
                  {d.first_name}
                </motion.span>
              ))}
            </div>
          </>
        )}

        {!loading && donors.length === 0 && (
          <div className="max-w-2xl" data-testid="donors-wall-empty">
            <p className="font-serif italic text-2xl md:text-3xl text-fg/60 leading-[1.4]">
              {en ? "The wall awaits its first names — as a bank awaits its first artworks." : "Le mur attend ses premiers prénoms — comme une berge attend ses premières œuvres."}
            </p>
          </div>
        )}
      </section>

      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="border-l-2 border-cinabre pl-6 md:pl-10 py-4">
          <div className="meta-label text-cinabre">§ {en ? "Join the wall" : "Rejoindre le mur"}</div>
          <h2 className="mt-4 font-serif font-light text-3xl md:text-5xl leading-[1] tracking-tight max-w-2xl">
            {en ? <>A donation, a first name, <em className="italic">a link in the Thread.</em></> : <>Un don, un prénom, <em className="italic">un maillon dans le Fil.</em></>}
          </h2>
          <p className="mt-5 text-fg/70 leading-relaxed max-w-xl">
            {en ? "You freely choose the amount — and whether your first name joins the wall. Tax receipt: 66% deduction for individuals under French law." : "Vous choisissez librement le montant — et si votre prénom rejoint le mur. Reçu fiscal : 66 % de déduction pour les particuliers."}
          </p>
          <Link
            to="/rejoindre#don"
            className="mt-8 inline-flex items-center gap-3 bg-cinabre text-bg px-8 py-4 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors group"
            data-testid="donors-wall-cta"
          >
            <Heart size={14} /> {en ? "Make a donation" : "Je fais un don"}
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </main>
  );
}
